import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import {
  DeletePhonemeInMediaHouseDocument,
  GetPhonemesFromMediaHouseDocument,
  type Phoneme,
  type PhonemeType,
} from "@/generated/graphql";
import { nonAudioTypeSlide } from "@/lib/constants/slides";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { HiPlus } from "react-icons/hi";
import { IoClose, IoSearch } from "react-icons/io5";
import { DisabledLockOverlay } from "./DisabledLockOverlay";
import { AddPhonemeModal } from "./phonics/AddPhonemeModal";
import { EditPhonemeModal } from "./phonics/EditPhonemeModal";
import { PhonemeTypeFilter } from "./phonics/PhonemeTypeFilter";
import { PhonemesGrid } from "./phonics/PhonemesGrid";
import { VoicesDialog } from "./phonics/VoicesDialog";

export function PhonicsPanel() {
  const [selectedPhonemeType, setSelectedPhonemeType] = useState<PhonemeType | null>(null);
  const [selectedPhoneme, setSelectedPhoneme] = useState<Phoneme | null>(null);
  const [showVoicesDialog, setShowVoicesDialog] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPhoneme, setEditingPhoneme] = useState<Phoneme | null>(null);
  const [search, setSearch] = useState("");

  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const audioDisabled = checkIfAudioDisabled(slide);
  const isPhonicsSlide = slide?.type === "phonics";

  const { data, loading, error } = useQuery(GetPhonemesFromMediaHouseDocument, {
    variables: { phonemeType: null },
    fetchPolicy: "cache-first",
    skip: audioDisabled,
  });

  const [deletePhoneme] = useMutation(DeletePhonemeInMediaHouseDocument, {
    refetchQueries: [
      { query: GetPhonemesFromMediaHouseDocument, variables: { phonemeType: null } },
    ],
  });

  const typeCounts = (data?.phonemesFromMediaHouse || []).reduce(
    (acc, phoneme) => {
      const type = phoneme.phonemeType;
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<PhonemeType, number>
  );

  const handlePhonemeClick = (phoneme: Phoneme) => {
    setSelectedPhoneme(phoneme);
    setShowVoicesDialog(true);
  };

  const handlePhonicsSlideSelect = (
    media: { src: string; id: string; filename: string },
    phonemeName: string,
    phonemeId: string
  ) => {
    if (!slide?.slideData) return;
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...slide.slideData, audio: media, phoneme: phonemeName, phonemeId },
      })
    );
  };

  const handleDeletePhoneme = async (phoneme: Phoneme) => {
    if (
      !window.confirm(
        `Delete phoneme "${phoneme.phoneme}"? This will also delete all its audio recordings.`
      )
    )
      return;
    try {
      await deletePhoneme({ variables: { id: phoneme.id } });
      toast.success(`Phoneme "${phoneme.phoneme}" deleted`);
    } catch {
      toast.error("Failed to delete phoneme");
    }
  };

  const allPhonemes = data?.phonemesFromMediaHouse ?? [];
  const filteredPhonics = allPhonemes.filter((p) => {
    const matchesType = !selectedPhonemeType || p.phonemeType === selectedPhonemeType;
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q || p.phoneme.toLowerCase().includes(q) || p.tags?.some((t) => t.toLowerCase().includes(q));
    return matchesType && matchesSearch;
  });

  return (
    <div className="p-3 relative">
      {/* Search + Add button row */}
      <div
        className={`mb-3 transition-all duration-300 ${
          audioDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
            <IoSearch size={14} className="text-slate-400 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search phoneme..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-slate-400 hover:text-slate-600"
              >
                <IoClose size={14} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            title="Add phoneme"
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <HiPlus size={16} />
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div
        className={`mb-3 transition-all duration-300 ${
          audioDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        <PhonemeTypeFilter
          activeType={selectedPhonemeType}
          onTypeChange={setSelectedPhonemeType}
          typeCounts={typeCounts}
        />
      </div>

      {/* Phonemes Grid */}
      <div
        className={`overflow-y-auto transition-all duration-300 ${
          audioDisabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        <PhonemesGrid
          phonemes={filteredPhonics}
          loading={loading}
          disabled={audioDisabled}
          error={error}
          onPhonemeClick={handlePhonemeClick}
          onEditPhoneme={(p) => setEditingPhoneme(p)}
          onDeletePhoneme={handleDeletePhoneme}
          showGrouped={!selectedPhonemeType && !search}
        />
      </div>

      {selectedPhoneme && (
        <VoicesDialog
          isOpen={showVoicesDialog}
          onClose={() => setShowVoicesDialog(false)}
          phoneme={selectedPhoneme}
          onSelect={isPhonicsSlide ? handlePhonicsSlideSelect : undefined}
        />
      )}

      <AddPhonemeModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />

      {editingPhoneme && (
        <EditPhonemeModal isOpen onClose={() => setEditingPhoneme(null)} phoneme={editingPhoneme} />
      )}

      {audioDisabled && (
        <DisabledLockOverlay
          title="Phonics Not Available"
          description="This slide type doesn't support audio elements"
        />
      )}
    </div>
  );
}

function checkIfAudioDisabled(slide: SlideFields | null): boolean {
  if (!slide) return false;
  return nonAudioTypeSlide[slide.type].includes(slide.knowledgeArea as never);
}
